import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import EmployeeAvatar from "../EmployeeAvatar";
import { getPresignedUrl } from "../../core/data/redux/awsBucketSlice";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage,
  uploadChatFiles,
} from "../../core/data/redux/nocOutsideJobChatSlice";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../utils/fileUtils";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  getValidNocType,
  formatChatMessageText,
} from "../../utils/nocChatUtils";

interface NOCChatComponentProps {
  nocRequestId: number;
  nocType: string; // e.g., "Foreign Visit", "Online Course", etc.
  employeeName?: string;
  employeeId?: string;
  employeeImage?: string;
  approverId?: string;
  isApprover?: boolean;
}

const NOCChatComponent: React.FC<NOCChatComponentProps> = ({
  nocRequestId,
  nocType,
  employeeName = "Employee",
  employeeId,
  employeeImage,
  approverId,
  isApprover: isApproverProp,
}) => {
  const dispatch = useDispatch<any>();

  // Chat state from Redux
  const {
    messages = [],
    attachments = [],
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat);

  // Get current user from Redux or localStorage
  const currentUser = useSelector((state: any) => state.auth?.user || state.user);
  const user = localStorage.getItem("user");
  const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
  const currentUserType =
    userJson?.role ||
    userJson?.user_type ||
    currentUser?.role ||
    currentUser?.user_type ||
    "";
  const currentUserId =
    userJson?.empId ||
    userJson?.empID ||
    currentUser?.empId ||
    currentUser?.empID ||
    "";

  const isCurrentUserApprover =
    Boolean(isApproverProp) ||
    (Boolean(approverId) &&
      Boolean(currentUserId) &&
      String(approverId).replace(/^0+/, "") ===
      String(currentUserId).replace(/^0+/, "")) ||
    currentUserType?.toUpperCase() === "APPROVER" ||
    currentUserType?.toUpperCase() === "ADMIN" ||
    currentUserType?.toUpperCase() === "HR";

  const isEmployee =
    currentUserType?.toUpperCase() === "EMPLOYEE" ||
    (!isCurrentUserApprover &&
      Boolean(employeeId) &&
      String(employeeId).replace(/^0+/, "") ===
      String(currentUserId).replace(/^0+/, ""));

  const hasApproverMessage =
    messages &&
    messages.length > 0 &&
    messages.some(
      (m: any) =>
        (approverId &&
          String(m.senderEmpId || "").replace(/^0+/, "") ===
          String(approverId).replace(/^0+/, "")) ||
        m.senderType === "approver" ||
        m.senderType === "admin"
    );

  const hasAnyMessage = Boolean(messages && messages.length > 0);

  const canChat =
    isCurrentUserApprover ||
    (!isEmployee ? true : (hasApproverMessage || hasAnyMessage));

  // Helper function to compare empIds (handles different formats)
  const isMessageFromCurrentUser = (messageSenderId: string): boolean => {
    const userEmpId = currentUserId;

    if (!userEmpId || !messageSenderId) {
      console.log('🔍 EmpId comparison - Missing data:', { userEmpId, messageSenderId });
      return false;
    }

    // Normalize both IDs (remove leading zeros, convert to string, trim)
    const normalizedUserId = String(userEmpId).trim().replace(/^0+/, '') || '0';
    const normalizedMessageId = String(messageSenderId).trim().replace(/^0+/, '') || '0';

    const isMatch = normalizedUserId === normalizedMessageId;

    return isMatch;
  };

  // Local state
  const [showChat, setShowChat] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messageInputRef = React.useRef<HTMLInputElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
    isPdf: boolean;
  } | null>(null);

  // 🎯 NEW: Document type modal state
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string>("");
  const [dismissedDocRequestId, setDismissedDocRequestId] = useState<string | null>(null);

  // 🎯 NEW: Document keywords for detection
  const DOCUMENT_KEYWORDS = [
    'document', 'file', 'upload', 'attach', 'attachment',
    'certificate', 'proof', 'scan', 'pdf', 'paper', 'submit',
    'send', 'provide', 'share'
  ];

  // 🎯 NEW: Check if message contains document keywords
  const containsDocumentKeyword = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return DOCUMENT_KEYWORDS.some(keyword =>
      lowerMessage.includes(keyword)
    );
  };

  // 🎯 Get latest unfulfilled document request from approver
  const latestDocRequest = React.useMemo(() => {
    const approverDocRequests = messages
      .filter((msg: ChatMessage) =>
        (msg.senderType === 'approver' || msg.senderType === 'admin') &&
        msg.documentType
      )
      .sort((a: ChatMessage, b: ChatMessage) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    if (approverDocRequests.length === 0) return null;

    const latestReq = approverDocRequests[0];
    const reqTime = new Date(latestReq.createdAt).getTime();

    // Check if the employee has already fulfilled this request (sent an attachment after the request was made)
    const hasBeenFulfilled = messages.some((msg: ChatMessage) => {
      const isEmployeeMsg =
        msg.senderType === 'employee' ||
        String(msg.senderEmpId || '').replace(/^0+/, '') === String(currentUserId || '').replace(/^0+/, '') ||
        String(msg.senderEmpId || '').replace(/^0+/, '') === String(employeeId || '').replace(/^0+/, '');

      const msgTime = new Date(msg.createdAt).getTime();
      const hasAttachment =
        (msg.attachments && msg.attachments.length > 0) ||
        (msg.documents && msg.documents.length > 0) ||
        Boolean(msg.message && msg.message.startsWith('📎'));

      return isEmployeeMsg && msgTime >= reqTime && hasAttachment;
    });

    if (hasBeenFulfilled) {
      return null;
    }

    return latestReq;
  }, [messages, currentUserId, employeeId]);

  // Fetch messages and attachments when chat opens (No recurring polling to prevent typing focus loss)
  useEffect(() => {
    if (showChat && nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));
    }
  }, [showChat, nocRequestId, nocType, dispatch]);

  // Fetch messages, unread count and attachments on component mount
  useEffect(() => {
    if (nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchUnreadCount(nocRequestId));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));
    }
  }, [nocRequestId, nocType, dispatch]);

  const isBoilerplateDocMessage = (text: string) => {
    if (!text) return false;
    const lower = text.trim().toLowerCase();
    return (
      lower === "please share education documents" ||
      lower === "please share advertisement documents" ||
      lower === "please share other documents" ||
      (lower.startsWith("please share") && lower.endsWith("documents"))
    );
  };

  // 🎯 UPDATED: Handle send message with mobile validation
  const handleSendMessage = async () => {
    if (!nocRequestId) return;

    const trimmedMsg = newMessage.trim();
    const isBoilerplate = isBoilerplateDocMessage(trimmedMsg);

    if (!trimmedMsg && selectedFiles.length === 0 && !selectedDocType) {
      return;
    }

    // Mobile parity (lines 669-674 in mobile StartChat.tsx):
    // If attachment(s) are present but no message text (or only boilerplate text), show friendly error
    // and keep the attachment in place.
    if (selectedFiles.length > 0 && (!trimmedMsg || isBoilerplate)) {
      if (isBoilerplate) {
        setNewMessage("");
      }
      const errorMsg = "Please add a message along with the attachment.";
      setInputError(errorMsg);
      toast.warning(errorMsg);
      setTimeout(() => messageInputRef.current?.focus(), 100);
      return;
    }

    // Normal message send
    setInputError(null);
    await sendMessageWithDocType(trimmedMsg, selectedDocType || null);
    setSelectedDocType(null);
  };

  // 🎯 NEW: Send message with document type
  const sendMessageWithDocType = async (
    message: string,
    docType: string | null
  ) => {
    try {
      setIsSending(true);
      let uploadedDocs: any[] = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            docType || latestDocRequest?.documentType
          );
        } else {
          toast.error("Failed to upload attached files. Please try again.");
          setIsSending(false);
          return;
        }
      }

      // Format message text with embedded attachment reference for backend @IsNotEmpty validator
      // Never send boilerplate share messages as the payload message with attachments
      let textToEmbed = isBoilerplateDocMessage(message) ? "" : message.trim();
      if (!textToEmbed && selectedFiles.length === 0 && docType) {
        textToEmbed = `Please share ${docType} Documents`;
      }
      const finalMessage = formatChatMessageText(textToEmbed, uploadedDocs);

      await dispatch(
        sendMessage({
          nocRequestId,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          files: selectedFiles,
          documentType: docType || (uploadedDocs.length > 0 ? latestDocRequest?.documentType : undefined),
        })
      ).unwrap();

      setNewMessage("");
      const wasWithAttachments = selectedFiles.length > 0;
      setSelectedFiles([]);
      dispatch(fetchMessages({ nocRequestId }));
      if (wasWithAttachments) {
        dispatch(fetchChatAttachments({ nocRequestId, nocType }));
      }
      toast.success(wasWithAttachments ? "Message and attachment sent successfully!" : "Message sent successfully!");
    } catch (error: any) {
      console.error("Error sending message:", error);
      const errMsg =
        (typeof error === "string" && error.trim()) ||
        error?.message ||
        error?.error ||
        (Array.isArray(error?.data) && error.data[0]) ||
        "Failed to send message";
      toast.error(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files) as File[];
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(", ")}`
          );
          return;
        }

        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        toast.error("File validation errors:\n" + errors.join("\n"));
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
        if (isBoilerplateDocMessage(newMessage)) {
          setNewMessage("");
        }
      }
    }

    event.target.value = "";
  };

  // 🎯 UPDATED: Confirm file upload with document type
  const confirmFileUpload = async (index: number) => {
    const file = selectedFiles[index];
    if (!file || !nocRequestId) return;

    const trimmedMsg = newMessage.trim();
    if (!trimmedMsg) {
      toast.warning("Please type a message with your attachment before sending.");
      return;
    }

    try {
      setUploadingFiles((prev) => new Set(prev).add(index));

      const uploadResult = await dispatch(uploadChatFiles([file]));

      if (uploadChatFiles.fulfilled.match(uploadResult)) {
        const uploadData = uploadResult.payload[0];

        // 🎯 Include document type if there's a pending request from approver
        const docType = latestDocRequest?.documentType;
        const uploadedDocs = prepareUploadedAttachments([uploadData], [file], docType);

        await dispatch(
          sendMessage({
            nocRequestId,
            message: trimmedMsg,
            attachments: uploadedDocs,
            documentType: docType || undefined
          })
        );

        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
        setNewMessage("");
        dispatch(fetchMessages({ nocRequestId }));

        // Show success message
        toast.success("File uploaded successfully!");
      } else {
        console.error("Upload failed:", uploadResult.payload);
        toast.error("Failed to upload file. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const cancelFileUpload = (index: number) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        setInputError(null);
      }
      return updated;
    });
  };

  const triggerFileInput = () => {
    // Only approvers request document types from employees.
    // Employees directly select their file to send / fulfill request.
    if (isCurrentUserApprover) {
      setShowDocTypeModal(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));
    } else {
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAttachmentClick = async (rawAttachment: any) => {
    await openChatAttachmentInExternalView(rawAttachment, nocRequestId, nocType, dispatch, attachments);
  };

  const handleDownloadClick = async (e: React.MouseEvent, rawAttachment: any) => {
    e.stopPropagation();
    await downloadChatAttachment(rawAttachment, nocRequestId, nocType, dispatch, attachments);
  };

  return (
    <>
      {/* Communication Section */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Communication</h5>
            {!canChat ? (
              <div className="text-muted small">
                <i className="ti ti-info-circle me-1"></i>
                Chat will be available once the approver initiates communication
              </div>
            ) : (
              <button
                className="btn btn-primary btn-sm position-relative"
                onClick={handleChatToggle}
              >
                <i className="ti ti-message me-1"></i>
                {showChat
                  ? "Close Chat"
                  : messages.length > 0
                    ? "View Chat"
                    : "Start Chat"}
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Chat Drawer - Full Screen Height Right Side Panel */}
      {showChat && (
        <>
          {/* Soft click-away backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{
              zIndex: 99990,
              backgroundColor: "rgba(15, 23, 42, 0.35)",
              backdropFilter: "blur(2px)",
              transition: "opacity 0.2s ease",
            }}
            onClick={handleChatToggle}
          />

          {/* Full Screen Height Right Side Panel */}
          <div
            className="position-fixed top-0 end-0 h-100 bg-white chat-drawer border-start d-flex flex-column"
            style={{
              width: isFullScreen ? "100vw" : "480px",
              maxWidth: "100vw",
              zIndex: 99999,
              borderRadius: "0px",
              borderLeft: "1px solid #e2e8f0",
              boxShadow: "-4px 0 24px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
              transition: "width 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Chat Header */}
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light flex-shrink-0">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-link text-dark text-decoration-none p-0 me-3"
                  onClick={handleChatToggle}
                  title="Close Chat"
                >
                  <i className="ti ti-arrow-left fs-20"></i>
                </button>
                <div>
                  <h5 className="mb-0 fs-15 fw-bold">Chat - NOC {nocType}</h5>
                  <small className="text-muted fs-12">{employeeName}</small>
                </div>
              </div>
              <div className="d-flex align-items-center gap-1.5">
                <button
                  className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center"
                  style={{ width: "30px", height: "30px", borderRadius: "5px" }}
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  title={isFullScreen ? "Restore to Side Panel" : "Expand to Full Screen"}
                >
                  <i className={`ti ${isFullScreen ? "ti-arrows-minimize" : "ti-arrows-maximize"} fs-14`}></i>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center"
                  style={{ width: "30px", height: "30px", borderRadius: "5px" }}
                  onClick={() => dispatch(fetchMessages({ nocRequestId }))}
                  disabled={chatLoading}
                  title="Refresh Messages"
                >
                  <i className={`ti ti-refresh fs-14 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center"
                  style={{ width: "30px", height: "30px", borderRadius: "5px" }}
                  onClick={handleChatToggle}
                  title="Close Chat"
                >
                  <i className="ti ti-x fs-16"></i>
                </button>
              </div>
            </div>
            {/* Chat Messages */}
            <div className="chat-messages-container flex-grow-1 overflow-auto p-4" style={{ backgroundColor: "#fcfcfc" }}>
              {(chatError || uploadError) && (
                <div className="alert alert-danger alert-dismissible">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      dispatch(clearError());
                      dispatch(clearUploadError());
                    }}
                  ></button>
                  {chatError || uploadError}
                </div>
              )}

              {chatLoading && messages.length === 0 ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                /* Mobile Empty State (Screenshot 3) */
                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center my-auto">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "72px",
                      height: "72px",
                      backgroundColor: "#f8fafc",
                      border: "1.5px solid #e2e8f0",
                      color: "#94a3b8",
                    }}
                  >
                    <i className="ti ti-messages" style={{ fontSize: "36px" }}></i>
                  </div>
                  <h6 className="fw-bold mb-1" style={{ color: "#64748b", fontSize: "15px" }}>
                    No messages yet
                  </h6>
                  <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                    Start the conversation below
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center my-2">
                    <span
                      className="badge px-3 py-1 text-muted"
                      style={{
                        backgroundColor: "rgba(0,0,0,0.05)",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}
                    >
                      Today
                    </span>
                  </div>
                  {messages.map((msg: ChatMessage) => {
                    // Check if this message is from the current logged-in user
                    const isCurrentUserMessage = isMessageFromCurrentUser(msg.senderEmpId);

                    return (
                      <div
                        key={msg.id}
                        className={`d-flex chat-message mb-3 ${isCurrentUserMessage ? "justify-content-end" : ""
                          }`}
                      >
                        {!isCurrentUserMessage && (
                          <div className="me-2">
                            <EmployeeAvatar
                              empName={msg.senderName || "Administrator"}
                              empImage={msg.senderImage}
                              empId={msg.senderEmpId}
                              size="sm"
                              showName={false}
                              showEmpId={false}
                              className=""
                            />
                          </div>
                        )}

                        <div
                          className="position-relative"
                          style={{ maxWidth: "78%", wordBreak: "break-word" }}
                        >
                          <div
                            className="p-2.5 shadow-sm"
                            style={{
                              borderRadius: isCurrentUserMessage ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                              backgroundColor: "#ffffff",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div className="d-flex align-items-center mb-1">
                              <small className="fw-bold" style={{ fontSize: "11px", lineHeight: "1.2", color: "#1e293b" }}>
                                {msg.senderName ||
                                  (msg.senderType === "employee"
                                    ? employeeName
                                    : "Administrator")}
                              </small>
                              <small className="ms-1.5 text-muted" style={{ fontSize: "10px", lineHeight: "1.2" }}>
                                ({msg.senderType})
                              </small>
                            </div>
                            {(() => {
                              const rawMsg = msg.message || "";
                              const paperclipIdx = rawMsg.indexOf("📎");
                              const hasPaperclip = paperclipIdx !== -1;

                              const displayMsgText = hasPaperclip
                                ? rawMsg.substring(0, paperclipIdx).trim()
                                : rawMsg.trim();

                              let extractedAttNames: string[] = [];
                              if (hasPaperclip) {
                                const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                                extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                              }

                              const attachmentsList: any[] =
                                msg.attachments && msg.attachments.length > 0
                                  ? msg.attachments
                                  : extractedAttNames.length > 0
                                    ? extractedAttNames
                                    : [];

                              return (
                                <>
                                  {displayMsgText && (
                                    <p
                                      className="mb-1 chat-message-content"
                                      style={{ lineHeight: "1.4", fontSize: "13px", color: "#1e293b" }}
                                    >
                                      {displayMsgText}
                                    </p>
                                  )}

                                  {/* 🎯 Document Type Badge */}
                                  {msg.documentType && (
                                    <div className="mt-1 pt-1 border-top border-opacity-25">
                                      <span
                                        className={`badge d-inline-flex align-items-center ${msg.documentType === 'Education'
                                          ? 'bg-info'
                                          : msg.documentType === 'Advertising' || msg.documentType === 'Advertisement'
                                            ? 'bg-warning text-dark'
                                            : 'bg-secondary'
                                          }`}
                                        style={{
                                          fontSize: '9px',
                                          padding: '1.5px 5px',
                                          fontWeight: 600,
                                          lineHeight: '1.2',
                                        }}
                                      >
                                        <i className="ti ti-file-certificate me-1" style={{ fontSize: "10px" }}></i>
                                        Requested: {msg.documentType}
                                      </span>
                                    </div>
                                  )}

                                  {/* 🎯 In-Bubble Attachment Card (Screenshot 4) */}
                                  {attachmentsList.length > 0 && (
                                    <div className="mt-1 pt-1">
                                      {attachmentsList.map((attachment: any, index: number) => {
                                        const rawName =
                                          typeof attachment === "string"
                                            ? attachment
                                            : attachment.fileName ||
                                            attachment.name ||
                                            attachment.FileName ||
                                            "Attachment";
                                        const cleanRaw = rawName.split("/").pop() || rawName;
                                        const parts = cleanRaw.split("-");
                                        const fileName = parts.length >= 3 && /^\d+$/.test(parts[0])
                                          ? parts.slice(2).join("-")
                                          : cleanRaw;
                                        const ext = (
                                          (typeof attachment === "object" ? attachment.fileExtension : "") ||
                                          fileName.split(".").pop() ||
                                          ""
                                        ).toLowerCase();
                                        const isPdf = ext === "pdf";
                                        const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);

                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center justify-content-between p-2 mt-1 attachment-card"
                                            style={{
                                              backgroundColor: "#f8fafc",
                                              border: "1px solid #e2e8f0",
                                              borderRadius: "10px",
                                              cursor: "pointer",
                                              maxWidth: "280px",
                                              transition: "all 0.15s ease",
                                            }}
                                            onClick={() => handleAttachmentClick(attachment)}
                                            title="Tap to open"
                                          >
                                            <div className="d-flex align-items-center overflow-hidden me-2" style={{ minWidth: 0 }}>
                                              <div
                                                className="d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                                style={{
                                                  width: "36px",
                                                  height: "36px",
                                                  backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                                                  borderRadius: "8px",
                                                  color: "#ffffff",
                                                }}
                                              >
                                                <i
                                                  className={isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"}
                                                  style={{ fontSize: "18px" }}
                                                ></i>
                                              </div>
                                              <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                <div
                                                  className="fw-bold text-truncate text-dark"
                                                  style={{ fontSize: "12.5px", lineHeight: "1.3" }}
                                                  title={fileName}
                                                >
                                                  {fileName}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: "10.5px" }}>
                                                  {ext.toUpperCase()} • Tap to open
                                                </div>
                                              </div>
                                            </div>
                                            <i className="ti ti-chevron-right text-muted flex-shrink-0" style={{ fontSize: "15px" }}></i>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              );
                            })()}

                            <div className="d-flex align-items-center justify-content-end gap-1 mt-1">
                              <small
                                className="text-muted"
                                style={{ fontSize: "10px", lineHeight: "1" }}
                              >
                                {formatMessageTime(msg.createdAt)}
                              </small>
                              {isCurrentUserMessage && (
                                <i className="ti ti-checks text-muted" style={{ fontSize: "12px" }}></i>
                              )}
                            </div>
                          </div>
                        </div>

                        {isCurrentUserMessage && (
                          <div className="ms-2">
                            <EmployeeAvatar
                              empName={msg.senderName || employeeName}
                              empImage={msg.senderImage || employeeImage}
                              empId={msg.senderEmpId || employeeId}
                              size="sm"
                              showName={false}
                              showEmpId={false}
                              className=""
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Chat Input */}
            <div className="chat-input-container">
              {/* Attachment Staging Area - Mobile Preview Card (Screenshot 2) */}
              {selectedFiles.length > 0 && (
                <div className="mb-2">
                  {selectedFiles.map((file, index) => {
                    const ext = file.name.split(".").pop()?.toLowerCase() || "";
                    const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
                    const isPdf = ext === "pdf";
                    const fileSizeKB = Math.round(file.size / 1024);

                    return (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between p-2 mb-1"
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "14px",
                        }}
                      >
                        <div
                          className="d-flex align-items-center overflow-hidden flex-grow-1"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const blobUrl = URL.createObjectURL(file);
                            window.open(blobUrl, "_blank");
                          }}
                          title="Tap to preview"
                        >
                          <div
                            className="d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{
                              width: "42px",
                              height: "42px",
                              backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                              borderRadius: "10px",
                              color: "#ffffff",
                            }}
                          >
                            <i
                              className={isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"}
                              style={{ fontSize: "22px" }}
                            ></i>
                          </div>
                          <div className="overflow-hidden me-2" style={{ minWidth: 0 }}>
                            <div
                              className="fw-bold text-truncate text-dark"
                              style={{ fontSize: "13.5px", lineHeight: "1.3" }}
                              title={file.name}
                            >
                              {file.name}
                            </div>
                            <div
                              className="text-muted text-truncate d-flex align-items-center gap-1.5 flex-wrap"
                              style={{ fontSize: "11px", marginTop: "2px" }}
                            >
                              {selectedDocType && (
                                <span
                                  className="badge px-1.5 py-0.5 fw-medium"
                                  style={{
                                    backgroundColor: "#fef2f2",
                                    color: "#b91c1c",
                                    border: "1px solid #fee2e2",
                                    fontSize: "9.5px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <i className="ti ti-file-text me-1"></i>
                                  {selectedDocType}
                                </span>
                              )}
                              <span>{ext.toUpperCase()} • {fileSizeKB}KB • Ready — Tap to preview</span>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          onClick={() => cancelFileUpload(index)}
                          style={{
                            width: "26px",
                            height: "26px",
                            backgroundColor: "#94a3b8",
                            color: "#ffffff",
                            border: "none",
                            padding: 0,
                          }}
                          title="Remove attachment"
                        >
                          <i className="ti ti-x" style={{ fontSize: "14px" }}></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Active Document Type Tag */}
              {selectedDocType && (
                <div className="px-3 pt-1 pb-1 flex-shrink-0">
                  <span
                    className="badge bg-white text-dark border d-inline-flex align-items-center gap-1.5 fs-11 fw-normal px-2.5 py-1"
                    style={{ borderRadius: "16px", borderColor: "#e2e8f0" }}
                  >
                    <i className="ti ti-file-text text-danger fs-12"></i>
                    <span>Document: <strong>{selectedDocType}</strong></span>
                    <button
                      type="button"
                      className="btn btn-link text-muted p-0 ms-1 d-flex align-items-center"
                      style={{ textDecoration: "none" }}
                      onClick={() => setSelectedDocType(null)}
                      title="Clear document type tag"
                    >
                      <i className="ti ti-x fs-10"></i>
                    </button>
                  </span>
                </div>
              )}

              {/* Input Error Message Banner (Direct Attachment Requires Message) */}
              {inputError && (
                <div className="mb-2 flex-shrink-0">
                  <div
                    className="d-flex align-items-center justify-content-between p-2 px-2.5 rounded-3 border shadow-xs"
                    style={{
                      backgroundColor: "#fef2f2",
                      borderColor: "#fecaca",
                      color: "#b91c1c",
                      fontSize: "12.5px",
                      fontWeight: 500,
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ti ti-alert-circle fs-16 flex-shrink-0" style={{ color: "#dc2626" }}></i>
                      <span>{inputError}</span>
                    </div>
                    <button
                      type="button"
                      className="btn p-0 border-0 bg-transparent text-danger d-flex align-items-center ms-2"
                      onClick={() => setInputError(null)}
                      title="Dismiss"
                    >
                      <i className="ti ti-x fs-12"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Chat Input Bar - Pill Container (Screenshots 2 & 3) */}
              <div
                className="d-flex align-items-center px-2 py-1"
                style={{
                  border: inputError ? "1.5px solid #f87171" : "1.5px solid #e2e8f0",
                  borderRadius: "28px",
                  backgroundColor: "#ffffff",
                  minHeight: "46px",
                  transition: "border-color 0.2s ease",
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelection}
                  multiple
                  className="d-none"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />

                <button
                  type="button"
                  className="btn btn-link p-0 text-muted ms-2 me-1 d-flex align-items-center justify-content-center flex-shrink-0"
                  onClick={triggerFileInput}
                  disabled={uploading || isSending}
                  style={{ width: "32px", height: "32px", textDecoration: "none" }}
                  title="Attach file"
                >
                  {uploading ? (
                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                  ) : (
                    <i className="ti ti-paperclip" style={{ fontSize: "20px", color: "#64748b" }}></i>
                  )}
                </button>

                <input
                  ref={messageInputRef}
                  type="text"
                  className="form-control border-0 shadow-none px-2"
                  style={{ fontSize: "13.5px", backgroundColor: "transparent" }}
                  placeholder="Start Typing..."
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    if (inputError) setInputError(null);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isSending}
                />

                <button
                  className="btn rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0 ms-1"
                  onClick={handleSendMessage}
                  disabled={isSending || uploading || (!newMessage.trim() && selectedFiles.length === 0 && !selectedDocType)}
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: (newMessage.trim() || selectedFiles.length > 0 || selectedDocType) ? "#c82333" : "#cbd5e1",
                    border: "none",
                    cursor: (newMessage.trim() || selectedFiles.length > 0 || selectedDocType) ? "pointer" : "default",
                    transition: "background-color 0.2s ease",
                  }}
                  title="Send message"
                >
                  {isSending || uploading ? (
                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                  ) : (
                    <i className="ti ti-send text-white" style={{ fontSize: "16px", marginLeft: "2px" }}></i>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 🎯 Mobile Document Type Selection Modal (Screenshot 1) */}
      {showDocTypeModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 100000, backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowDocTypeModal(false)}
          />
          <div
            className="modal fade show d-block"
            style={{ zIndex: 100001 }}
            tabIndex={-1}
            role="dialog"
            onClick={() => setShowDocTypeModal(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              style={{ maxWidth: "380px", margin: "1.75rem auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-content border-0 shadow-lg"
                style={{ borderRadius: "20px", overflow: "hidden", padding: "18px 20px" }}
              >
                <div className="mb-3">
                  <h5 className="fw-bold mb-1" style={{ fontSize: "17px", color: "#1e293b" }}>
                    Select Document Type
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                    Which type of document are you requesting from the employee?
                  </p>
                </div>

                <div className="d-flex flex-column gap-2 mb-3">
                  {/* Option 1: Education Documents */}
                  <div
                    className="d-flex align-items-center justify-content-between p-2 rounded-3"
                    style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                    onClick={() => {
                      setSelectedDocType("Education");
                      setNewMessage("Please share education document");
                      setInputError(null);
                      setShowDocTypeModal(false);
                      setTimeout(() => messageInputRef.current?.focus(), 150);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          backgroundColor: "#f1f5f9",
                          fontSize: "20px",
                        }}
                      >
                        📚
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "14px", color: "#1e293b" }}>
                          Education Documents
                        </div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          Certificates, degrees, transcripts, etc.
                        </div>
                      </div>
                    </div>
                    <i className="ti ti-chevron-right text-muted" style={{ fontSize: "16px" }}></i>
                  </div>

                  {/* Option 2: Advertisement Documents */}
                  <div
                    className="d-flex align-items-center justify-content-between p-2 rounded-3"
                    style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                    onClick={() => {
                      setSelectedDocType("Advertisement");
                      setNewMessage("Please share advertisement document");
                      setInputError(null);
                      setShowDocTypeModal(false);
                      setTimeout(() => messageInputRef.current?.focus(), 150);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          backgroundColor: "#f1f5f9",
                          fontSize: "20px",
                        }}
                      >
                        📢
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "14px", color: "#1e293b" }}>
                          Advertisement Documents
                        </div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          Job posting, newspaper clipping etc.
                        </div>
                      </div>
                    </div>
                    <i className="ti ti-chevron-right text-muted" style={{ fontSize: "16px" }}></i>
                  </div>

                  {/* Option 3: Other Documents */}
                  <div
                    className="d-flex align-items-center justify-content-between p-2 rounded-3"
                    style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                    onClick={() => {
                      setSelectedDocType("Other");
                      setNewMessage("Please share other document");
                      setInputError(null);
                      setShowDocTypeModal(false);
                      setTimeout(() => messageInputRef.current?.focus(), 150);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          backgroundColor: "#f1f5f9",
                          fontSize: "20px",
                        }}
                      >
                        📄
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "14px", color: "#1e293b" }}>
                          Other Documents
                        </div>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          Any other supporting documents.
                        </div>
                      </div>
                    </div>
                    <i className="ti ti-chevron-right text-muted" style={{ fontSize: "16px" }}></i>
                  </div>
                </div>

                <hr className="my-2" style={{ borderColor: "#f1f5f9" }} />

                {/* Option 4: Send Attachment */}
                <div
                  className="d-flex align-items-center justify-content-between p-2 rounded-3 mt-1"
                  style={{ cursor: "pointer", transition: "background 0.15s ease" }}
                  onClick={() => {
                    setSelectedDocType(null);
                    setShowDocTypeModal(false);
                    if (isBoilerplateDocMessage(newMessage)) {
                      setNewMessage("");
                    }
                    setTimeout(() => fileInputRef.current?.click(), 150);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        backgroundColor: "#fee2e2",
                        color: "#dc2626",
                      }}
                    >
                      <i className="ti ti-paperclip" style={{ fontSize: "20px" }}></i>
                    </div>
                    <div>
                      <div className="fw-bold" style={{ fontSize: "14px", color: "#dc2626" }}>
                        Send Attachment
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        Supported: PDF, JPG, PNG
                      </div>
                    </div>
                  </div>
                  <i className="ti ti-chevron-right" style={{ fontSize: "16px", color: "#dc2626" }}></i>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          .chat-message {
            margin-bottom: 15px !important;
          }
          
          .chat-message-content {
            word-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
          }
          
          .chat-drawer {
            display: flex;
            flex-direction: column;
            animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          }
          
          .chat-messages-container {
            flex: 1;
            overflow-y: auto;
            padding: 1rem;
            min-height: 0;
          }
          
          .chat-input-container {
            flex-shrink: 0;
            padding: 1rem;
            border-top: 1px solid #dee2e6;
            background-color: #f8f9fa;
          }
          
          .chat-messages-container::after {
            content: '';
            display: block;
            height: 20px;
          }
          
          .file-attachment-preview {
            max-width: 100%;
            overflow-x: auto;
          }
          
          .bg-warning-light {
            background-color: #fff3cd !important;
          }
          
          .file-confirm-btn {
            transition: all 0.2s ease;
          }
          
          .file-confirm-btn:hover {
            transform: scale(1.1);
          }
          
          .attachment-link {
            transition: all 0.2s ease;
          }
          
          .attachment-link:hover {
            opacity: 0.8 !important;
            transform: translateX(2px);
          }

          /* Document Type Modal Styles */
          .hover-shadow {
            transition: all 0.2s ease;
            cursor: pointer;
          }
          
          .hover-shadow:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            transform: translateY(-1px);
          }
          
          .cursor-pointer {
            cursor: pointer;
          }
          
          .form-check-input:checked ~ .form-check-label {
            color: var(--bs-primary);
          }
          
          .modal-backdrop.show {
            opacity: 0.5;
          }
          
          .modal.show .modal-dialog {
            animation: slideDown 0.3s ease-out;
          }
          
          @keyframes slideDown {
            from {
              transform: translateY(-50px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Media / Lightbox Preview Modal */}
      {previewMedia && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
          style={{ zIndex: 9999, backdropFilter: "blur(4px)" }}
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="bg-white rounded-3 shadow-lg p-3 position-relative"
            style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
              <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "60%" }}>
                <i className="ti ti-file-text me-1.5 text-primary"></i>
                {previewMedia.title}
              </h6>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary py-1 px-2 fs-12"
                  onClick={() => openChatAttachmentInExternalView(previewMedia.url, nocRequestId, nocType, dispatch, attachments)}
                  title="Open in dedicated external tab"
                >
                  <i className="ti ti-external-link me-1"></i>External View
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1 px-2 fs-12"
                  onClick={() => downloadChatAttachment(previewMedia.url, nocRequestId, nocType, dispatch, attachments)}
                  title="Download file"
                >
                  <i className="ti ti-download me-1"></i>Download
                </button>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewMedia(null)}
                ></button>
              </div>
            </div>
            <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
              {previewMedia.isImage ? (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.title}
                  className="img-fluid rounded"
                  style={{ maxHeight: "75vh", objectFit: "contain" }}
                />
              ) : (
                <iframe
                  src={`${previewMedia.url}#toolbar=1`}
                  title={previewMedia.title}
                  style={{ width: "80vw", height: "70vh", border: "none" }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NOCChatComponent;
